import {AnimatePresence, animate} from '@sail/engine';
import {Button, FocusScope, Spinner, view} from '@sail/ui';
import {useCallback, useState} from 'react';
import {FormattedMessage, MessageDescriptor} from 'react-intl';
import {useTheme} from '../ThemeProvider';

const getLinkConfirmationButtonStyles = (
  theme: ReturnType<typeof useTheme>,
) => ({
  font: 'body.small' as const,
  fontWeight: theme.secondaryButtonFontWeight,
  keyline: 'none',
  ':hover': {keyline: 'none'},
  ':active': {keyline: 'none'},
});

type Props = {
  title: MessageDescriptor | string;
  description: MessageDescriptor | string;
  confirmLabel: MessageDescriptor | string;
  cancelLabel: MessageDescriptor | string;
  open: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
};

export function FullDrawerConfirmation({
  title,
  description,
  confirmLabel,
  cancelLabel,
  open,
  onConfirm,
  onCancel,
}: Props) {
  const theme = useTheme();
  const isLinkAudience = theme.audience === 'link';
  const [pending, setPending] = useState(false);

  const handleConfirm = useCallback(async () => {
    setPending(true);
    try {
      await onConfirm();
    } finally {
      setPending(false);
    }
  }, [onConfirm]);

  const handleCancel = useCallback(async () => {
    setPending(true);
    try {
      await onCancel();
    } finally {
      setPending(false);
    }
  }, [onCancel]);

  let titleMessage;
  if (typeof title === 'string') {
    titleMessage = title;
  } else {
    titleMessage = <FormattedMessage {...title} />;
  }

  let descriptionMessage;
  if (typeof description === 'string') {
    descriptionMessage = description;
  } else {
    descriptionMessage = <FormattedMessage {...description} />;
  }

  let cancelLabelMessage;
  if (typeof cancelLabel === 'string') {
    cancelLabelMessage = cancelLabel;
  } else {
    cancelLabelMessage = <FormattedMessage {...cancelLabel} />;
  }

  let confirmLabelMessage;
  if (typeof confirmLabel === 'string') {
    confirmLabelMessage = confirmLabel;
  } else {
    confirmLabelMessage = <FormattedMessage {...confirmLabel} />;
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <view.div
            data-testid="conversations-widget-full-drawer-confirm"
            css={{
              position: 'absolute',
              inset: 'space.0',
            }}
          />

          <view.div
            css={{
              backgroundColor: 'surface',
              opacity: 0.95,
              position: 'absolute',
              inset: 'space.0',
              zIndex: 10,
            }}
            uses={[
              animate.inout({
                duration: 150,
                opacity: 0,
                easing: 'ease-in-out',
              }),
            ]}
          >
            <view.div
              css={{
                stack: 'x',
                alignX: 'center',
                alignY: 'center',
                position: 'absolute',
                inset: 'space.0',
                paddingX: 'xlarge',
                paddingY: '10px',
              }}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: '10px',
                }}
              >
                <FocusScope autoFocus restoreFocus>
                  <view.div
                    css={{stack: 'y', gap: 'xxsmall', paddingBottom: 'xsmall'}}
                  >
                    <view.h2
                      data-testid="conversations-widget-confirmation-title"
                      css={
                        isLinkAudience
                          ? {
                              font: 'body.large.emphasized',
                              marginBottom: 0,
                            }
                          : {font: 'heading.small', marginBottom: 0}
                      }
                    >
                      {titleMessage}
                    </view.h2>

                    <view.div
                      data-testid="conversations-widget-confirmation-description"
                      css={{font: 'body.small'}}
                    >
                      {descriptionMessage}
                    </view.div>
                  </view.div>
                  <view.div css={{stack: 'x', gap: 'small'}}>
                    <Button
                      data-testid="conversations-widget-confirmation-cancel"
                      type="secondary"
                      onPress={handleCancel}
                      disabled={pending}
                      css={
                        isLinkAudience
                          ? {
                              ...getLinkConfirmationButtonStyles(theme),
                              backgroundColor:
                                theme.secondaryButtonBackground ??
                                theme.surfaceHoverColor,
                            }
                          : undefined
                      }
                    >
                      {cancelLabelMessage}
                    </Button>
                    <Button
                      data-testid="conversations-widget-confirmation-confirm"
                      type="destructive"
                      onPress={handleConfirm}
                      disabled={pending}
                      css={
                        isLinkAudience
                          ? getLinkConfirmationButtonStyles(theme)
                          : undefined
                      }
                    >
                      {pending ? <Spinner /> : null}
                      {confirmLabelMessage}
                    </Button>
                  </view.div>
                </FocusScope>
              </view.div>
            </view.div>
          </view.div>
        </>
      )}
    </AnimatePresence>
  );
}
